import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/Loader"
import React, { useEffect, useMemo, useState } from "react"
import { FaCheck } from "react-icons/fa"
import { FaX } from "react-icons/fa6"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

const Leaves = () => {
  document.title = "Leaves | Vintage Infra Equipment Sol PVT LTD"

  const [loading, setLoading] = useState(false)

  /* ================== PAGINATION + SEARCH ================== */
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  /* ================== LEAVE DATA ================== */
  const [leaveData, setLeaveData] = useState([])

  /* ================== USERS (EMPLOYEES) ================== */
  const [employees, setEmployees] = useState([])

  // 👉 PLACEHOLDER FUNCTION – ADD API LATER
  const fetchEmployees = async () => {
    const APIdata = await fetchData(setLoading, "users")
    if (APIdata?.success) {
      setEmployees(APIdata?.data)
    }
  }

  const fetchLeaves = async () => {
    const APIdata = await fetchData(setLoading, "leave")
    if (APIdata?.success) {
      setLeaveData(APIdata?.data)
    }
  }

  useEffect(() => {
    fetchEmployees()
    fetchLeaves()
  }, [])

  /* ================== ADD LEAVE MODAL ================== */
  const [modalOpen, setModalOpen] = useState(false)

  const [newLeave, setNewLeave] = useState({
    employeeId: "",
    leaveType: "",
    fromDate: "",
    toDate: "",
    leaveDate: "",
    workedDate: "",
    reason: "",
  })

  const handleChange = e => {
    const { name, value } = e.target
    if (name === "leaveType") {
      setNewLeave(prev => ({
        ...prev,
        leaveType: value,
        fromDate: "",
        toDate: "",
        leaveDate: "",
        workedDate: "",
      }))
      return
    }
    setNewLeave(prev => ({ ...prev, [name]: value }))
  }

  /* ================== ADD LEAVE SUBMIT ================== */
  const handleAddLeave = async () => {
    const {
      employeeId,
      leaveType,
      fromDate,
      toDate,
      leaveDate,
      workedDate,
      reason,
    } = newLeave

    if (!employeeId) {
      return Swal.fire("Required", "Please select Employee", "warning")
    }

    if (!leaveType) {
      return Swal.fire("Required", "Please select Leave Type", "warning")
    }

    if (!reason.trim()) {
      return Swal.fire("Required", "Please enter Reason", "warning")
    }

    if (leaveType === "paidOff") {
      if (!leaveDate) {
        return Swal.fire("Required", "Please select Leave Date", "warning")
      }

      if (!workedDate) {
        return Swal.fire("Required", "Please select Worked Date", "warning")
      }

      if (new Date(leaveDate) > new Date(workedDate)) {
        return Swal.fire(
          "Invalid Dates",
          "Worked Date cannot be before Leave Date",
          "warning"
        )
      }
    }

    if (leaveType === "casualLeave" || leaveType === "sickLeave") {
      if (!fromDate) {
        return Swal.fire("Required", "Please select From Date", "warning")
      }

      if (!toDate) {
        return Swal.fire("Required", "Please select To Date", "warning")
      }

      if (new Date(fromDate) > new Date(toDate)) {
        return Swal.fire(
          "Invalid Dates",
          "From Date cannot be after To Date",
          "warning"
        )
      }
    }

    // ✅ Payload (clean & safe)
    const payload = {
      userId: employeeId,
      type: leaveType,
      reason,

      ...(leaveType === "paidOff"
        ? {
            leaveDate,
            adjustmentDate: workedDate,
          }
        : {
            fromDate,
            toDate,
          }),
    }

    const APIdata = await FetchPostData(setLoading, "leave", payload)

    if (APIdata?.data?.success) {
      Swal.fire("Success", APIdata?.data?.message, "success")

      setModalOpen(false)
      setNewLeave({
        employeeId: "",
        leaveType: "",
        fromDate: "",
        toDate: "",
        leaveDate: "",
        workedDate: "",
        reason: "",
      })

      fetchLeaves()
    } else {
      Swal.fire(
        "Error",
        APIdata?.response?.data?.message || "Something went wrong",
        "error"
      )
    }
  }

  /* ================== SEARCH ================== */
  const filteredData = useMemo(() => {
    return leaveData.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(searchString.toLowerCase())
    )
  }, [leaveData, searchString])

  /* ================== PAGINATION ================== */
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize
  const paginatedData = filteredData.slice(start, start + pageSize)

  /* ================== STATUS ACTIONS ================== */

  const handleApprove = id => {
    Swal.fire({
      icon: "info",
      title: "Approve Leave?",
      showCancelButton: true,
      confirmButtonText: "Approve",
    }).then(async res => {
      if (res.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "leave", id, {
          status: true,
        })

        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Leave Approved Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          fetchLeaves()
        } else {
          Swal.fire({
            title: "Error approving Leave.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const handleReject = id => {
    Swal.fire({
      icon: "info",
      title: "Reject Leave?",
      showCancelButton: true,
      confirmButtonText: "Reject",
    }).then(async res => {
      if (res.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "leave", id, {
          status: false,
        })

        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Leave Rejected Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          fetchLeaves()
        } else {
          Swal.fire({
            title: "Error rejecting Leave.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const statusBadge = status => {
    if (status === true)
      return (
        <Badge color="success" className="p-2 fs-6">
          Approved
        </Badge>
      )
    if (status === false)
      return (
        <Badge color="danger" className="p-2 fs-6">
          Rejected
        </Badge>
      )
    return (
      <Badge color="warning" className="p-2 fs-6">
        Pending
      </Badge>
    )
  }

  /* ================== UI ================== */
  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Leave Management" breadcrumbItem="Leaves" />

      <Card>
        <CardBody>
          {/* HEADER */}
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex gap-2">
              <Input
                type="select"
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                className="rounded-4 w-auto pe-5"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Input>
              <Input
                placeholder="Search..."
                value={searchString}
                onChange={e => setSearchString(e.target.value)}
                className="rounded-4 w-auto"
              />
            </div>
            <Button className="btn-blue" onClick={() => setModalOpen(true)}>
              + Leave
            </Button>
          </div>

          {/* TABLE */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Employee</th>
                <th>Leave Type</th>
                <th>From - To</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={item.id}>
                  <td>{start + index + 1}</td>
                  <td>{item?.user.name}</td>
                  <td>
                    {item.type === "paidOff"
                      ? "Paid Off"
                      : item.type === "casualLeave"
                      ? "Casual Leave"
                      : item.type === "sickLeave"
                      ? "Sick Leave"
                      : "--"}
                  </td>
                  <td>
                    {item.type === "paidOff"
                      ? `Leave: ${item.leaveDate} | Worked: ${item.adjustmentDate}`
                      : `${item.fromDate} → ${item.toDate}`}
                  </td>
                  <td>{item.reason}</td>
                  <td>{statusBadge(item.status)}</td>
                  <td>
                    {item.status === null ? (
                      <>
                        <Button
                          color="success"
                          onClick={() => handleApprove(item.id)}
                          className="me-2"
                        >
                          <FaCheck size={14} />
                        </Button>
                        <Button
                          color="danger"
                          onClick={() => handleReject(item.id)}
                        >
                          <FaX size={14} />
                        </Button>
                      </>
                    ) : (
                      "--"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {/* ================= Pagination ================= */}
          <nav className="d-flex justify-content-between mt-3">
            <h6>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h6>

            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      {/* ================== ADD LEAVE MODAL ================== */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>Add Leave</ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md={12}>
              <label>
                Employee <span className="text-danger">*</span>
              </label>
              <Input
                type="select"
                value={newLeave.employeeId}
                onChange={e => {
                  setNewLeave(prev => ({
                    ...prev,
                    employeeId: e.target.value,
                  }))
                }}
              >
                <option value="">Select Employee </option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </Input>
            </Col>

            <Col md={12}>
              <label>
                Leave Type <span className="text-danger">*</span>
              </label>
              <Input
                type="select"
                name="leaveType"
                value={newLeave.leaveType}
                onChange={handleChange}
              >
                <option value="">Select Leave Type</option>
                <option value="casualLeave">Casual Leave</option>
                <option value="sickLeave">Sick Leave</option>
                <option value="paidOff">Paid Off</option>
              </Input>
            </Col>

            {newLeave.leaveType === "paidOff" ? (
              <>
                <Col md={6}>
                  <label>
                    Leave Date <span className="text-danger">*</span>
                  </label>
                  <Input
                    type="date"
                    name="leaveDate"
                    value={newLeave.leaveDate}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={6}>
                  <label>
                    Worked Date <span className="text-danger">*</span>
                  </label>
                  <Input
                    type="date"
                    name="workedDate"
                    value={newLeave.workedDate}
                    onChange={handleChange}
                  />
                </Col>
              </>
            ) : (
              <>
                <Col md={6}>
                  <label>From Date</label>
                  <Input
                    type="date"
                    name="fromDate"
                    value={newLeave.fromDate}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={6}>
                  <label>To Date</label>
                  <Input
                    type="date"
                    name="toDate"
                    value={newLeave.toDate}
                    onChange={handleChange}
                  />
                </Col>
              </>
            )}

            <Col md={12}>
              <label>
                Reason <span className="text-danger">*</span>
              </label>
              <Input
                name="reason"
                value={newLeave.reason}
                onChange={handleChange}
              />
            </Col>

            <Col md={12} className="text-end">
              <Button className="w-25 btn-blue me-2" onClick={handleAddLeave}>
                Submit Leave
              </Button>
              <Button
                color="secondary"
                className="w-25"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default Leaves
